// OpenRouter API Service (via Cloudflare Worker Proxy)
import { API_CONFIG, getWorkerUrl, getAppPassword } from '../config/api.config';
import { storage } from './storage';
import Logger from './logger';
import { TCM_SYSTEM_PROMPT, buildUserPrompt } from './tcm-prompts';

// Fallback models if primary fails (using latest available models)
const FALLBACK_MODELS = [
    'google/gemini-3-flash-preview',      // Latest Gemini Flash - Fast & Good
    'google/gemini-3-pro-preview',        // Latest Gemini Pro - Most Intelligent
    'anthropic/claude-sonnet-4.5',        // Latest Claude - Code & Logic
    'openai/gpt-5-mini',                  // GPT-5 Mini - Fast & Efficient
    'qwen/qwen3-vl-235b-a22b-thinking'    // Qwen 3 VL - Strong Vision
];

// Retry configuration
const RETRY_CONFIG = {
    maxRetries: 2,
    baseDelay: 1000
};

// Image compression utility
async function compressImage(base64Data, maxSize = API_CONFIG.MAX_IMAGE_SIZE) {
    Logger.debug('IMAGE', 'Compressing image...');

    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            let { width, height } = img;

            // Scale down if too large
            const maxDim = 1024;
            if (width > maxDim || height > maxDim) {
                const ratio = Math.min(maxDim / width, maxDim / height);
                width *= ratio;
                height *= ratio;
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            // Compress with quality adjustment
            let quality = 0.8;
            let result = canvas.toDataURL('image/jpeg', quality);

            // Further compress if still too large
            while (result.length > maxSize && quality > 0.3) {
                quality -= 0.1;
                result = canvas.toDataURL('image/jpeg', quality);
            }

            Logger.debug('IMAGE', `Compressed: ${(result.length / 1024).toFixed(1)}KB, quality: ${quality}`);
            resolve(result);
        };
        img.src = base64Data;
    });
}

// Attempt to repair truncated JSON
function repairTruncatedJson(jsonStr) {
    // Count open and close braces/brackets
    let braceCount = 0;
    let bracketCount = 0;
    let inString = false;
    let escape = false;

    for (let i = 0; i < jsonStr.length; i++) {
        const char = jsonStr[i];
        if (escape) {
            escape = false;
            continue;
        }
        if (char === '\\' && inString) {
            escape = true;
            continue;
        }
        if (char === '"') {
            inString = !inString;
            continue;
        }
        if (!inString) {
            if (char === '{') braceCount++;
            else if (char === '}') braceCount--;
            else if (char === '[') bracketCount++;
            else if (char === ']') bracketCount--;
        }
    }

    // If in the middle of a string, close it
    if (inString) {
        jsonStr += '"';
    }

    // Add missing closing brackets/braces
    while (bracketCount > 0) {
        jsonStr += ']';
        bracketCount--;
    }
    while (braceCount > 0) {
        jsonStr += '}';
        braceCount--;
    }

    return jsonStr;
}

// Parse AI response to extract JSON
function parseAIResponse(content) {
    Logger.debug('PARSE', 'Parsing AI response...');

    let jsonStr = content;

    // Remove markdown code blocks if present
    const jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)```/);
    if (jsonMatch) {
        jsonStr = jsonMatch[1].trim();
    }

    // Try to find JSON object
    const objectMatch = jsonStr.match(/\{[\s\S]*\}/);
    if (objectMatch) {
        jsonStr = objectMatch[0];
    }

    try {
        let parsed;
        try {
            parsed = JSON.parse(jsonStr);
        } catch (firstError) {
            // Try to repair truncated JSON
            Logger.warn('PARSE', 'First parse failed, attempting repair', { error: firstError.message });
            const repaired = repairTruncatedJson(jsonStr);
            parsed = JSON.parse(repaired);
            Logger.info('PARSE', 'JSON repair successful');
        }
        Logger.debug('PARSE', 'Successfully parsed JSON');

        // Validate required fields
        if (!parsed.overallScore || !parsed.tongue || !parsed.syndrome) {
            Logger.warn('PARSE', 'Missing required fields, using defaults');
        }

        // Default risk categories (lower scores = better)
        const defaultRiskCategories = {
            stress: 30, fatigue: 35, sleep_quality: 30,
            cardiovascular: 25, cerebrovascular: 20, blood_pressure: 30, dyslipidemia: 35,
            digestive_capacity: 30, gerd: 25, hyperacidity: 25,
            liver_burden: 30, kidney_burden: 25, endocrine: 25, calcium_deficiency: 30,
            blood_sugar: 30, immune_capacity: 35, allergy: 25, obesity: 30
        };

        // Default system scores
        const defaultSystemScores = {
            nervous: 30, cardiovascular: 25, digestive: 28, functional: 27, metabolic: 30
        };

        // Default constitution scores
        const defaultConstitutionScores = {
            pinghe: 60, qixu: 35, yangxu: 25, yinxu: 28,
            tanshi: 30, shire: 25, xueyu: 22, qiyu: 30, tebing: 15
        };

        // Ensure all required fields exist with defaults
        const result = {
            overallScore: parsed.overallScore || 75,
            tongue: {
                color: parsed.tongue?.color || 'Đang phân tích',
                shape: parsed.tongue?.shape || 'Bình thường',
                coating: parsed.tongue?.coating || 'Trắng mỏng',
                moisture: parsed.tongue?.moisture || 'Nhuận',
                explanation: parsed.tongue?.explanation || 'Đang cập nhật...',
            },
            face: {
                overall: parsed.face?.overall || 'Hồng nhuận',
                forehead: parsed.face?.forehead || 'Bình thường',
                eyeArea: parsed.face?.eyeArea || 'Bình thường',
                cheeks: parsed.face?.cheeks || 'Bình thường',
                lips: parsed.face?.lips || 'Hồng nhuận',
                explanation: parsed.face?.explanation || 'Đang cập nhật...',
            },
            syndrome: {
                primary: {
                    name: parsed.syndrome?.primary?.name || 'Đang xác định',
                    chinese: parsed.syndrome?.primary?.chinese || '',
                    confidence: parsed.syndrome?.primary?.confidence || 70,
                },
                yinYang: parsed.syndrome?.yinYang || 'Cân bằng',
                coldHeat: parsed.syndrome?.coldHeat || 'Trung tính',
                patterns: parsed.syndrome?.patterns || [
                    { name: 'Đang phân tích...', source: 'AI', match: 70 }
                ],
                description: parsed.syndrome?.description || '',
            },
            // New fields for comprehensive report
            riskCategories: {
                ...defaultRiskCategories,
                ...(parsed.riskCategories || {})
            },
            systemScores: {
                ...defaultSystemScores,
                ...(parsed.systemScores || {})
            },
            constitution: {
                scores: {
                    ...defaultConstitutionScores,
                    ...(parsed.constitution?.scores || {})
                },
                primary: parsed.constitution?.primary || {
                    name_vi: 'Bình hòa chất',
                    name_cn: '平和质',
                    score: 60,
                    icon: '⚖️'
                },
                secondary: parsed.constitution?.secondary || [],
                status: parsed.constitution?.status || 'balanced',
                details: parsed.constitution?.details || {}
            },
            // Handle both old (array) and new (object) recommendation formats
            recommendations: parsed.recommendations || [
                'Duy trì lối sống lành mạnh',
                'Ngủ đủ giấc',
                'Tham khảo ý kiến chuyên gia'
            ],
        };

        return result;
    } catch (parseError) {
        Logger.error('PARSE', 'Failed to parse JSON', { error: parseError.message, content: jsonStr.substring(0, 200) });
        throw new Error('Không thể phân tích phản hồi từ AI. Vui lòng thử lại.');
    }
}

// Main analysis function - now uses Cloudflare Worker proxy
export async function analyzeWithAI(images, answers, analysisType) {
    const model = storage.getSelectedModel();
    const workerUrl = getWorkerUrl();
    const appPassword = getAppPassword();

    Logger.api('API', 'Starting AI analysis via Worker', { model, analysisType, workerUrl });

    if (!appPassword) {
        throw new Error('Chưa xác thực. Vui lòng nhập mật khẩu ứng dụng.');
    }

    // Prepare images
    const imageContents = [];
    for (const [type, base64] of Object.entries(images)) {
        if (base64) {
            Logger.debug('IMAGE', `Processing ${type} image`);
            const compressed = await compressImage(base64);
            imageContents.push({
                type: 'image_url',
                image_url: {
                    url: compressed,
                    detail: 'high'
                }
            });
        }
    }

    if (imageContents.length === 0) {
        throw new Error('Không có hình ảnh để phân tích');
    }

    // Build messages
    const messages = [
        { role: 'system', content: TCM_SYSTEM_PROMPT },
        {
            role: 'user',
            content: [
                { type: 'text', text: buildUserPrompt(images, answers, analysisType) },
                ...imageContents
            ]
        }
    ];

    Logger.api('API', 'Sending request to Worker', {
        model,
        imageCount: imageContents.length,
        endpoint: `${workerUrl}/api/analyze`
    });

    const startTime = Date.now();

    // Helper function to make API call with specific model
    const makeApiCall = async (modelToUse) => {
        Logger.api('API', `Trying model: ${modelToUse}`);

        const response = await fetch(`${workerUrl}/api/analyze`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-App-Password': appPassword,
            },
            body: JSON.stringify({
                model: modelToUse,
                messages: messages,
                max_tokens: 4000,  // Increased from 2000 to prevent truncated responses
                temperature: 0.3,
            })
        });

        const elapsed = Date.now() - startTime;
        Logger.api('API', `Response received in ${elapsed}ms`, { status: response.status, model: modelToUse });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            Logger.error('API', 'API Error', { ...errorData, model: modelToUse });

            if (response.status === 401) {
                throw new Error('AUTH_ERROR: Mật khẩu không hợp lệ. Vui lòng đăng nhập lại.');
            } else if (response.status === 429) {
                throw new Error('RATE_LIMIT: Đã vượt quá giới hạn API. Đang thử model khác...');
            } else if (response.status === 400) {
                throw new Error('MODEL_ERROR: Model không hỗ trợ. Đang thử model khác...');
            }
            throw new Error(errorData.error || `API_ERROR: ${response.status}`);
        }

        const data = await response.json();
        Logger.api('API', 'API Response', {
            model: data.model,
            usage: data.usage,
            finishReason: data.choices?.[0]?.finish_reason
        });

        const content = data.choices?.[0]?.message?.content;
        if (!content) {
            Logger.error('API', 'Empty content in response', {
                hasChoices: !!data.choices,
                choicesLength: data.choices?.length,
                finishReason: data.choices?.[0]?.finish_reason,
                message: data.choices?.[0]?.message,
                fullResponse: JSON.stringify(data).substring(0, 1000)
            });
            const finishReason = data.choices?.[0]?.finish_reason;
            if (finishReason === 'content_filter') {
                throw new Error('CONTENT_FILTER: Nội dung bị lọc. Thử chụp ảnh khác.');
            } else if (finishReason === 'length') {
                throw new Error('LENGTH_ERROR: Phản hồi quá dài.');
            }
            throw new Error('EMPTY_RESPONSE: AI không trả về nội dung.');
        }

        return content;
    };

    // Retry with fallback models
    const modelsToTry = [model, ...FALLBACK_MODELS.filter(m => m !== model)];
    let lastError = null;

    for (let i = 0; i < modelsToTry.length; i++) {
        const currentModel = modelsToTry[i];
        try {
            Logger.info('API', `Attempt ${i + 1}/${modelsToTry.length} with model: ${currentModel}`);

            const content = await makeApiCall(currentModel);
            Logger.debug('PARSE', 'Raw AI response', { content: content.substring(0, 500) });

            // Parse JSON from response
            const result = parseAIResponse(content);
            result._model = currentModel; // Track which model succeeded
            result._rawResponse = content; // Store raw response for audit
            Logger.info('ANALYSIS', 'Analysis completed successfully', { model: currentModel });

            return result;

        } catch (error) {
            lastError = error;
            Logger.warn('API', `Model ${currentModel} failed`, { error: error.message });

            // Don't retry for authentication errors
            if (error.message.includes('AUTH_ERROR')) {
                throw error;
            }

            // Wait before trying next model (exponential backoff)
            if (i < modelsToTry.length - 1) {
                const delay = RETRY_CONFIG.baseDelay * Math.pow(2, i);
                Logger.info('API', `Waiting ${delay}ms before trying next model...`);
                await new Promise(r => setTimeout(r, delay));
            }
        }
    }

    // All models failed
    Logger.error('API', 'All models failed', {
        modelsTrieds: modelsToTry,
        lastError: lastError?.message,
        elapsed: Date.now() - startTime
    });

    throw new Error(`Không thể phân tích. Đã thử ${modelsToTry.length} model. Lỗi: ${lastError?.message || 'Unknown error'}`);
}

// Test API connection via Worker
export async function testConnection() {
    const workerUrl = getWorkerUrl();
    const appPassword = getAppPassword();

    if (!appPassword) {
        return { success: false, message: 'Chưa xác thực' };
    }

    Logger.api('API', 'Testing Worker connection...', { workerUrl });

    try {
        // First test worker health
        const healthResponse = await fetch(`${workerUrl}/health`);
        if (!healthResponse.ok) {
            return { success: false, message: 'Worker không phản hồi' };
        }

        // Test with actual API call
        const response = await fetch(`${workerUrl}/api/analyze`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-App-Password': appPassword,
            },
            body: JSON.stringify({
                model: storage.getSelectedModel(),
                messages: [{ role: 'user', content: 'Hello, respond with just "OK"' }],
                max_tokens: 10,
            })
        });

        if (response.ok) {
            Logger.info('API', 'Connection test successful');
            return { success: true, message: 'Kết nối Worker thành công!' };
        } else {
            const error = await response.json().catch(() => ({}));
            Logger.error('API', 'Connection test failed', error);
            return { success: false, message: error.error || `Lỗi ${response.status}` };
        }
    } catch (error) {
        Logger.error('API', 'Connection test error', { error: error.message });
        return { success: false, message: 'Không thể kết nối Worker: ' + error.message };
    }
}

// Generate mock results for fallback
export function generateMockResults(answers) {
    Logger.debug('MOCK', 'Generating mock results with comprehensive data');

    // Calculate scores based on answers
    let baseScore = 85;
    let stressScore = 30;
    let fatigueScore = 35;
    let sleepScore = 30;

    if (answers?.fatigue > 7) {
        baseScore -= 5;
        fatigueScore = 65;
    }
    if (answers?.stress > 7) {
        baseScore -= 5;
        stressScore = 70;
    }
    if (answers?.sleepQuality < 4) {
        baseScore -= 5;
        sleepScore = 60;
    }

    return {
        overallScore: Math.max(60, Math.min(95, baseScore + Math.floor(Math.random() * 10) - 5)),
        tongue: {
            color: 'Hồng nhạt',
            shape: 'Hơi bệu, có dấu răng',
            coating: 'Trắng mỏng',
            moisture: 'Nhuận',
            explanation: 'Lưỡi có màu hồng nhạt với rêu trắng mỏng cho thấy khí huyết điều hòa. Hình dạng hơi bệu có dấu răng gợi ý tỳ khí hư nhẹ. Độ ẩm nhuận cho thấy tân dịch đầy đủ.',
        },
        face: {
            overall: 'Hồng nhuận',
            forehead: 'Bình thường',
            eyeArea: 'Quầng nhẹ',
            cheeks: 'Bình thường',
            lips: 'Hồng nhuận',
            explanation: 'Sắc mặt hồng nhuận cho thấy khí huyết lưu thông tốt. Quầng mắt nhẹ có thể do thiếu ngủ hoặc stress kéo dài.',
        },
        syndrome: {
            primary: { name: 'Tỳ Khí Hư', chinese: '脾气虚', confidence: 87 },
            yinYang: 'Cân bằng',
            coldHeat: 'Trung tính',
            patterns: [
                { name: 'Lưỡi nhạt, có vết răng', source: 'Thiệt chẩn', match: 95 },
                { name: 'Mệt mỏi, chán ăn', source: 'Bảng câu hỏi', match: 88 },
                { name: 'Rêu trắng hơi dày', source: 'Thiệt chẩn', match: 82 },
            ],
            description: 'Tỳ Khí Hư là tình trạng suy giảm chức năng vận hóa của Tỳ, thường biểu hiện bởi mệt mỏi, chán ăn, và tiêu hóa kém.',
        },
        // 18 Risk Categories
        riskCategories: {
            stress: stressScore,
            fatigue: fatigueScore,
            sleep_quality: sleepScore,
            cardiovascular: 25,
            cerebrovascular: 20,
            blood_pressure: 30,
            dyslipidemia: 35,
            digestive_capacity: 45,
            gerd: 25,
            hyperacidity: 30,
            liver_burden: 35,
            kidney_burden: 25,
            endocrine: 28,
            calcium_deficiency: 30,
            blood_sugar: 32,
            immune_capacity: 40,
            allergy: 22,
            obesity: 28
        },
        // System Scores
        systemScores: {
            nervous: Math.round((stressScore + fatigueScore + sleepScore) / 3),
            cardiovascular: 28,
            digestive: 40,
            functional: 30,
            metabolic: 32
        },
        // Constitution
        constitution: {
            scores: {
                pinghe: 45,
                qixu: 68,
                yangxu: 35,
                yinxu: 30,
                tanshi: 42,
                shire: 28,
                xueyu: 25,
                qiyu: 38,
                tebing: 18
            },
            primary: {
                name_vi: 'Khí hư chất',
                name_cn: '气虚质',
                score: 68,
                icon: '🔋',
                color: '#F59E0B'
            },
            secondary: [
                { type: 'tanshi', score: 42, info: { name_vi: 'Đàm thấp chất', icon: '💧' } },
                { type: 'qiyu', score: 38, info: { name_vi: 'Khí uất chất', icon: '😔' } }
            ],
            status: 'deviation',
            details: {
                characteristics: {
                    physical: ['Thể hình gầy hoặc hơi béo', 'Cơ bắp nhão, thiếu săn chắc'],
                    physiological: ['Hay mệt mỏi, hụt hơi', 'Dễ ra mồ hôi khi vận động']
                },
                tongue_signs: { description: 'Lưỡi nhạt màu, có dấu răng ở rìa' },
                pulse_signs: { description: 'Mạch hư nhược, vô lực' },
                disease_tendency: ['Cảm cúm thường xuyên', 'Mệt mỏi mãn tính', 'Sa nội tạng'],
                treatment_principle: 'Bổ khí kiện tỳ (补气健脾)',
                dietary: {
                    recommended: ['Hoàng kỳ', 'Đảng sâm', 'Đại táo', 'Thịt gà', 'Gạo nếp'],
                    avoid: ['Củ cải sống', 'Đồ lạnh', 'Trà đậm', 'Rượu bia']
                }
            }
        },
        // Structured recommendations
        recommendations: {
            diet: [
                'Ăn nhiều thực phẩm bổ tỳ như khoai lang, gạo nếp',
                'Tránh đồ lạnh, đồ sống',
                'Nên nấu chín kỹ thức ăn'
            ],
            lifestyle: [
                'Ngủ đủ 7-8 tiếng mỗi đêm',
                'Tập thể dục nhẹ 30 phút/ngày',
                'Tránh làm việc quá sức'
            ],
            acupoints: [
                '足三里 (ST36) - Túc tam lý',
                '气海 (CV6) - Khí hải',
                '关元 (CV4) - Quan nguyên'
            ],
            herbs: [
                'Hoàng kỳ - Bổ khí',
                'Đảng sâm - Kiện tỳ',
                'Bạch truật - Vận hóa thấp'
            ],
            general: [
                'Duy trì tâm trạng thoải mái',
                'Tham khảo ý kiến thầy thuốc YHCT'
            ]
        },
        _isMock: true,
        _timestamp: new Date().toISOString()
    };
}

export default {
    analyzeWithAI,
    testConnection,
    generateMockResults,
};
